import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Button,
  EmptyState,
  Input,
  Select,
  StatusPill,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
  type Density,
  type Tone,
} from "@wmart/ui";
import { api, ApiError } from "../lib/api-client.js";
import { useToast } from "../lib/toast.js";
import type { StaffDTO, StaffListResponse, StaffRole } from "@wmart/shared-types";
import { StaffRoleValues, staffRoleLabel } from "@wmart/shared-types";

type StaffStatus = "active" | "invite_pending" | "deactivated";

function statusOf(s: StaffDTO): StaffStatus {
  if (s.deleted_at) return "deactivated";
  if (s.invite_pending) return "invite_pending";
  return "active";
}

const STATUS_TONE: Record<StaffStatus, Tone> = {
  active: "success",
  invite_pending: "warning",
  deactivated: "neutral",
};

const STATUS_LABEL: Record<StaffStatus, string> = {
  active: "Active",
  invite_pending: "Invite pending",
  deactivated: "Deactivated",
};

/**
 * Team roster. Mirrors CustomersList: debounced search, role filter, cursor
 * "Load more". The roster includes deactivated staff (status badge) — the API
 * does not filter them out. Create/edit are admin-only on the API; the "Add
 * staff member" CTA and row links are visible to any staff role but the
 * server returns 403 for non-admins (no client-side role gating; the toast is
 * the single source of truth — same disclosure as CompanyLocationsList).
 */
export function TeamList() {
  const { notify, notifyProblem } = useToast();
  const [items, setItems] = useState<StaffDTO[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [roleFilter, setRoleFilter] = useState<StaffRole | "all">("all");
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [density, setDensity] = useState<Density>("default");

  useEffect(() => {
    const stored = window.localStorage.getItem("wmart.density");
    if (stored === "compact" || stored === "default" || stored === "cosy") {
      setDensity(stored);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 250);
    return () => clearTimeout(t);
  }, [q]);

  async function fetchPage(reset: boolean, cursor: string | null) {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("limit", "50");
      if (roleFilter !== "all") params.set("role", roleFilter);
      if (debouncedQ.trim()) params.set("q", debouncedQ.trim());
      if (cursor) params.set("cursor", cursor);
      const data = await api.get<StaffListResponse>(
        `/v1/admin/staff?${params.toString()}`,
      );
      setItems((prev) => (reset ? data.items : [...prev, ...data.items]));
      setNextCursor(data.nextCursor);
    } catch (err) {
      if (err instanceof ApiError && err.problem) notifyProblem(err.problem);
      else notify("error", "Failed to load staff");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void fetchPage(true, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleFilter, debouncedQ]);

  const hasAnyFilter = roleFilter !== "all" || debouncedQ.trim() !== "";

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-64">
            <Input
              type="search"
              label="Search"
              placeholder="Search by name or email…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <div className="w-48">
            <Select
              label="Role"
              value={roleFilter}
              onChange={(e) =>
                setRoleFilter(e.target.value as StaffRole | "all")
              }
            >
              <option value="all">All roles</option>
              {StaffRoleValues.map((r) => (
                <option key={r} value={r}>
                  {staffRoleLabel(r)}
                </option>
              ))}
            </Select>
          </div>
          {hasAnyFilter ? (
            <Button
              variant="link"
              size="sm"
              onClick={() => {
                setRoleFilter("all");
                setQ("");
              }}
            >
              Clear
            </Button>
          ) : null}
        </div>
        <Link to="/team/new">
          <Button>Add staff member</Button>
        </Link>
      </div>

      {items.length === 0 && !loading ? (
        <EmptyState
          icon="user"
          title="No staff match"
          body="Adjust filters, or add a new staff member."
        />
      ) : (
        <>
          <Table density={density}>
            <THead>
              <TR>
                <TH>Name / email</TH>
                <TH>Role</TH>
                <TH>Department</TH>
                <TH>Status</TH>
              </TR>
            </THead>
            <TBody>
              {items.map((s) => {
                const st = statusOf(s);
                return (
                  <TR key={s.id}>
                    <TD>
                      <Link
                        to={`/team/${s.id}`}
                        className="text-ink-900 hover:underline"
                      >
                        {s.name ?? s.email ?? "(no name)"}
                      </Link>
                      {s.name && s.email ? (
                        <div className="text-body-sm text-ink-500">
                          {s.email}
                        </div>
                      ) : null}
                    </TD>
                    <TD>{staffRoleLabel(s.staff_role)}</TD>
                    <TD className="text-ink-500">{s.department ?? "—"}</TD>
                    <TD>
                      <StatusPill tone={STATUS_TONE[st]}>
                        {STATUS_LABEL[st]}
                      </StatusPill>
                    </TD>
                  </TR>
                );
              })}
            </TBody>
          </Table>

          {nextCursor ? (
            <div className="flex justify-center">
              <Button
                variant="secondary"
                onClick={() => void fetchPage(false, nextCursor)}
                disabled={loading}
                loading={loading}
              >
                Load more
              </Button>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
